import React, { useEffect, useRef, useState } from 'react';

interface SearchProgressProps {
  /** True while a search is in flight. Resets the clock on each new search. */
  active: boolean;
  /**
   * Whether this is a Custom Filter run. Those are UNCAPPED — the reporting surface
   * people export from — so a broad one can legitimately run for minutes, and the
   * patience note only makes sense there. A plain PRO lookup that took minutes would
   * be a fault, not something to reassure the user about.
   */
  longRunning?: boolean;
  /**
   * Server-side budget in seconds. Shown once the run is clearly slow, so the wait has
   * a stated end rather than being open-ended.
   */
  budgetSeconds?: number;
}

/** Seconds since `active` last became true; frozen at 0 while inactive. */
function useElapsedSeconds(active: boolean): number {
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef<number>(0);

  useEffect(() => {
    if (!active) {
      setElapsed(0);
      return;
    }
    // performance.now(), not Date.now(): immune to a clock adjustment mid-search, which
    // would otherwise show a negative or jumped elapsed time.
    startedAt.current = performance.now();
    setElapsed(0);
    const id = window.setInterval(() => {
      setElapsed(Math.floor((performance.now() - startedAt.current) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [active]);

  return elapsed;
}

// A search that has been running this long is not "about to finish" — past here the
// user needs telling that waiting is expected rather than just watching a spinner.
const PATIENCE_AFTER_SECONDS = 5;

/**
 * Loading state for the search results grid: a spinner, an elapsed clock, and — for an
 * uncapped Custom Filter — a note that a broad filter takes a while.
 *
 * The clock exists because the alternative is indistinguishable from a hang. A filter
 * over the full order archive can run for minutes by design, and a static "Searching…"
 * gives the user no way to tell progress from a stall, so they reload — which abandons
 * the query server-side without stopping it.
 */
const SearchProgress: React.FC<SearchProgressProps> = ({
  active, longRunning = false, budgetSeconds,
}) => {
  const elapsed = useElapsedSeconds(active);
  if (!active) return null;

  const slow = elapsed >= PATIENCE_AFTER_SECONDS;

  return (
    <div
      className="flex flex-col items-center justify-center gap-2 py-10"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2.5">
        <svg
          className="h-5 w-5 animate-spin text-purple-600 dark:text-purple-400"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-90"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
          Searching…
        </span>
        {/* Only once there is something to report, so a fast search doesn't flash "0s". */}
        {elapsed > 0 && (
          <span className="text-sm tabular-nums text-muted">
            {elapsed}s
          </span>
        )}
      </div>

      {longRunning && slow && (
        <p className="max-w-md text-center text-xs text-muted">
          Broad filters can take a while — this one is still running, so please wait
          rather than reloading.
          {budgetSeconds ? ` It will stop on its own after ${budgetSeconds}s.` : ''}
        </p>
      )}
    </div>
  );
};

export default SearchProgress;
